<template>
    <v-row no-gutters>
        <v-col cols="5" xl="5" lg="6" md="12">
            <InputsTree
                :inputs="inputsData"
                :data="data"
                @setSnackbar="setSnackbar"
            />
            <!-- SNACKBAR -->
            <v-snackbar
                v-model="snackbar"
                :color="snackcolor"
                :timeout="snacktimeout"
                position="absolute"
                :absolute="true"
                :multi-line="true"
                style="white-space: pre-line"
                vertical
            >
                {{ snacktext }}
                <template v-slot:actions>
                    <v-btn variant="text" @click="snackbar = false">
                        {{ $t("labels.close") }}
                    </v-btn>
                </template>
            </v-snackbar>
        </v-col>
        <v-col cols="7" xl="7" lg="6" md="12">
            <h4>{{ $t("strings.properties-to-set") }}</h4>
            <h6>{{ $t("strings.instructions-for-setting-properties") }}</h6>
            <v-sheet
                class="overflow-y-auto pt-4"
                :max-height="data.parentContentHeight"
            >
                <v-form ref="form" v-model="valid" lazy-validation class="pa-2">
                    <v-sheet>
                        <v-row
                            dense
                            v-if="
                                operatorParameters &&
                                operatorParameters.length > 0
                            "
                        >
                            <v-col
                                cols="12"
                                v-for="(param, index) in operatorParameters"
                                :key="index"
                            >
                                <InputBlock
                                    v-if="param.datatype"
                                    :key="index"
                                    :paramValues="param"
                                    @bindInput="bindInput"
                                >
                                    <template
                                        #dynamicParam="slotProps"
                                        v-if="
                                            param.name === 'source' &&
                                            formData.dynamicParams
                                        "
                                    >
                                        <component
                                            v-for="(
                                                component, index
                                            ) in dynamicComponents.filter(
                                                (comp) =>
                                                    comp.dynamicParams
                                                        .valuesFrom ===
                                                    'source',
                                            )"
                                            :key="index"
                                            :is="component"
                                            :dynamicParams="
                                                component.dynamicParams
                                            "
                                            :data="slotProps"
                                        />
                                    </template>
                                </InputBlock>
                                <SelectBlock
                                    v-if="param.options"
                                    :paramValues="param"
                                    :initValue="param.options[0]"
                                />
                                <InputBlockDatasource
                                    v-if="operatorType === 'dataSources'"
                                    :key="index"
                                    :paramValues="param"
                                    @bindInput="bindInput"
                                >
                                    <template
                                        #dynamicParam="slotProps"
                                        v-if="
                                            param.name === 'source' &&
                                            formData.dynamicParams
                                        "
                                    >
                                        <component
                                            v-for="(
                                                component, index
                                            ) in dynamicComponents.filter(
                                                (comp) =>
                                                    comp.dynamicParams
                                                        .valuesFrom ===
                                                    'source',
                                            )"
                                            :key="index"
                                            :is="component"
                                            :dynamicParams="
                                                component.dynamicParams
                                            "
                                            :data="slotProps"
                                        />
                                    </template>
                                </InputBlockDatasource>
                            </v-col>
                            <template v-if="formData.dynamicParams">
                                <v-col
                                    cols="12"
                                    v-for="(
                                        component, index
                                    ) in dynamicComponents"
                                    :key="index"
                                >
                                    <component
                                        v-if="
                                            !component.dynamicParams.valuesFrom
                                        "
                                        :key="index"
                                        :is="component"
                                        :dynamicParams="component.dynamicParams"
                                    />
                                </v-col>
                            </template>
                        </v-row>
                        <v-row dense v-else>
                            <template v-if="formData.dynamicParams">
                                <v-col cols="12">
                                    <component
                                        v-for="(
                                            component, index
                                        ) in dynamicComponents"
                                        :key="index"
                                        :is="component"
                                        :dynamicParams="component.dynamicParams"
                                    />
                                </v-col>
                            </template>
                        </v-row>
                    </v-sheet>
                </v-form>
            </v-sheet>
        </v-col>
    </v-row>
</template>
<script>
import { inject, provide, reactive, toRefs, watch, computed } from "vue";
import { ref, watchEffect, markRaw } from "vue";
import { retrievesParameters, getUniqueKeys } from "@/mixins/common-utils.js";
import InputsTree from "../blocks/InputsListTree.vue";
import InputBlock from "../blocks/InputBlock.vue";
import InputBlockDatasource from "../blocks/InputBlockDatasource.vue";
import SelectBlock from "../blocks/SelectBlock.vue";
import KeyValueBlock from "../blocks/KeyValueBlock.vue";
import RecursiveListTree from "../blocks/RecursiveListTree.vue";
import SimpleTreeView from "../blocks/SimpleTreeView.vue";
import SimpleTreeNode from "../blocks/SimpleTreeNode.vue";
import { useI18n } from "vue-i18n";

export default {
    name: "OperatorDefault",
    props: ["target", "data", "operatorInputs"],
    emits: ["updateFormData", "bindInput"],
    components: {
        InputsTree,
        InputBlock,
        InputBlockDatasource,
        SelectBlock,
    },
    setup(props, { emit }) {
        const { t } = useI18n();
        let snackbar = ref(false);
        let snacktext = ref(t("strings.default-message-snackbar"));
        let snackcolor = ref("green");
        let snacktimeout = ref(2000);
        const valid = inject("valid");
        const operatorType = ref(props.target.type);
        const operatorParameters = computed(() => {
            return props.target.userData.parameters;
        });

        //Creates a deep copy, unlinked of target.userData
        const formData = reactive(
            JSON.parse(JSON.stringify(props.target.userData)) || {},
        );
        //and provide it to child components
        provide("formData", formData);

        const setSnackbar = (
            value = snackbar.value,
            text = snacktext.value,
            color = snackcolor.value,
            timeout = snacktimeout.value,
        ) => {
            snackbar.value = value;
            snacktext.value = text;
            snacktext.value = text;
            snackcolor.value = color;
            snacktimeout.value = timeout;
        };

        // Vite resolves glob imports statically at build time, so all block chunks
        // are bundled with correct asset URLs regardless of the microfrontend base path.
        // This avoids the 404 errors caused by dynamic string-interpolated imports
        // being resolved against the root config host instead of this microfrontend's host.
        const blockModules = import.meta.glob([
            '../blocks/*.vue',
            '!../blocks/InputsListTree.vue',
            '!../blocks/InputBlock.vue',
            '!../blocks/InputBlockDatasource.vue',
            '!../blocks/SelectBlock.vue',
            '!../blocks/KeyValueBlock.vue',
            '!../blocks/RecursiveListTree.vue',
            '!../blocks/SimpleTreeView.vue',
            '!../blocks/SimpleTreeNode.vue',
        ]);

        const staticBlockComponents = {
            InputsListTree: InputsTree,
            InputBlock,
            InputBlockDatasource,
            SelectBlock,
            KeyValueBlock,
            RecursiveListTree,
            SimpleTreeView,
            SimpleTreeNode,
        };

        // This function is executed whenever a reactive dependency (`formData.dynamicParams`) changes.
        // If `formData.dynamicParams` is truthy, it maps over the array and returns an array of promises.
        // Each promise is the result of dynamically importing a Vue.js component based on the `blockId` property of the current parameter object.
        // When each import promise resolves, it adds additional parameters to the default export of the module and returns it.
        // `Promise.all()` is then used to wait for all the import promises to resolve.
        // When all promises have resolved, the resulting array of components is assigned to `dynamicComponents.value`.
        // This ensures that all components are loaded in the order they appear in `formData.dynamicParams`.
        const dynamicComponents = ref([]);
        watchEffect(() => {
            if (formData.dynamicParams) {
                const promises = formData.dynamicParams.map((block) => {
                    const staticComponent = staticBlockComponents[block.blockId];
                    if (staticComponent) {
                        return Promise.resolve(
                            markRaw({
                                ...staticComponent,
                                dynamicParams: block,
                            }),
                        );
                    }

                    const modulePath = `../blocks/${block.blockId}.vue`;
                    const loader = blockModules[modulePath];
                    if (!loader) {
                        console.error(`Block not found: ${block.blockId}`);
                        return Promise.resolve(null);
                    }
                    return loader()
                        .then((module) => {
                            return markRaw({
                                ...module.default,
                                dynamicParams: block,
                            });
                        })
                        .catch((err) => {
                            console.error(err);
                        });
                });
                //To ensures original order
                Promise.all(promises)
                    .then((components) => {
                        dynamicComponents.value = components.filter(Boolean);
                    })
                    .catch((err) => {
                        console.error(err);
                    });
            }
        });

        //Computes and return inputs linked to the operator
        let inputsData = computed(() => {
            return props.operatorInputs;
        });

        watch(
            formData,
            (newFormData) => {
                emit("updateFormData", newFormData);
            },
            { deep: true, immadiate: true },
        );

        /**
         * The bindInput function links an input element to a specific parameter and configures the options for that parameter.
         * @param {Object} input - The input object from which unique keys are extracted.
         * @param {string} bindedElement - The name property value in a parameter object.
         *  Because selectParams is a reference to the parameter object within formData.parameters, these changes are also reflected in formData.parameters.
         *  As a result, the input element is linked to the specified parameter and its options are configured based on the input.
         */
        function bindInput(input, bindedElement) {
            const selectParams = retrievesParameters(
                bindedElement, //this should be the name property value in a parameter object
                formData.parameters, //these are all operator parameters
            );
            selectParams.property = ""; //Reset property value
            selectParams.options = getUniqueKeys(input); //Assign values to options of the Select
        }

        return {
            snackbar,
            snacktext,
            snackcolor,
            snacktimeout,
            setSnackbar,
            inputsData,
            formData,
            ...toRefs(formData),
            valid,
            bindInput,
            dynamicComponents,
            operatorType,
            operatorParameters,
        };
    },
};
</script>
